<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.BOOKMAKER.REQUEST_ACCOUNT_CONFIRMATION"
    :handler="() => $refs.modal.show()"
  />
  <VBusHelper
    :reactOn="$constants.EVENTS.BOOKMAKER.BOOKMAKER_ID_CONFIRMED"
    :handler="() => $refs.modal.hide('confirmed_action')"
  />
  <VBusHelper
    :reactOn="$constants.EVENTS.BOOKMAKER.REQUEST_BOOKMAKER_ID_CHANGE"
    :handler="() => $refs.modal.hide()"
  />
  <DialogComponent
    ref="modal"
    type="modal"
    mainClass="fixed z-[61]"
    @closed="
      ({ source }) =>
        source !== 'confirmed_action' &&
        $bus.emit(
          $constants.EVENTS.BOOKMAKER.BOOKMAKER_ID_CONFIRMATION_CANCELED
        )
    "
  >
    <div class="p-6">
      <!-- Loading State -->
      <div
        v-if="
          $stores.bookmaker?.state?.bookmakerData?.[
            `${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmaker}_${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmakerId}`
          ]?.loading
        "
        class="flex flex-col items-center py-8"
      >
        <div class="w-16 h-16 mb-6 relative">
          <div class="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
          <div class="absolute inset-0 border-4 border-t-primary rounded-full animate-spin"></div>
        </div>
        <div class="text-xl font-bold text-gray-800 mb-2">
          Vérification du compte
        </div>
        <div class="text-gray-500">
          {{ $stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmakerName }}
        </div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="
          $stores.bookmaker?.state?.bookmakerData?.[
            `${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmaker}_${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmakerId}`
          ]?.error
        "
        class="flex flex-col items-center py-6"
      >
        <div class="w-16 h-16 mb-6 rounded-full bg-red-100 flex items-center justify-center">
          <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </div>
        <div class="text-xl font-bold text-gray-800 mb-3 text-center">
          Erreur de vérification
        </div>
        <div class="text-red-500 text-center mb-6">
          {{
            $stores.bookmaker?.state?.bookmakerData?.[
              `${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmaker}_${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmakerId}`
            ]?.error
          }}
        </div>
      </div>

      <!-- Success State -->
      <div
        v-else-if="
          $stores.bookmaker?.state?.bookmakerData?.[
            `${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmaker}_${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmakerId}`
          ]?.data
        "
        class="flex flex-col items-center py-6"
      >
        <div class="w-16 h-16 mb-6 rounded-full bg-green-100 flex items-center justify-center">
          <svg class="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <div class="text-xl font-bold text-gray-800 mb-3 text-center">
          Confirmez votre compte
        </div>
        <div class="text-center text-gray-600 mb-2">
          Nous allons utiliser le compte
        </div>
        <div class="bg-green-50 border border-green-200 rounded-2xl px-6 py-4 mb-6">
          <div class="font-bold text-green-800 text-lg">
            {{
              $stores.bookmaker?.state?.bookmakerData?.[
                `${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmaker}_${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmakerId}`
              ]?.data?.name
            }}
          </div>
          <div class="text-green-600 text-sm">
            ID: {{ $stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmakerId }}
          </div>
        </div>
        <div class="text-gray-600 text-center">
          Voulez-vous continuer ?
        </div>
      </div>

      <!-- Buttons for Success State -->
      <div
        class="flex flex-col gap-3 mt-6"
        v-if="
          $stores.bookmaker?.state?.bookmakerData?.[
            `${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmaker}_${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmakerId}`
          ]?.data
        "
      >
        <button
          class="w-full py-4 px-6 text-white font-bold text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden group bg-secondary"
          @click="
            $bus.emit($constants.EVENTS.BOOKMAKER.BOOKMAKER_ID_CONFIRMED, {
              bookmakerId:
                $stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmakerId,
              bookmaker:
                $stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmaker
            })
          "
        >
          <span class="relative z-10 flex items-center justify-center gap-2">
            Confirmer
            <svg class="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
            </svg>
          </span>
        </button>
        <button
          class="w-full py-4 px-6 font-semibold rounded-2xl border-2 transition-all duration-300 flex items-center justify-center gap-3 hover:bg-gray-50 active:scale-[0.98] border-secondary-600 text-secondary-600"
          @click="
            $bus.emit($constants.EVENTS.BOOKMAKER.REQUEST_BOOKMAKER_ID_CHANGE, {
              bookmakerId:
                $stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmakerId,
              bookmaker:
                $stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmaker
            })
          "
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Changer d'ID
        </button>
      </div>

      <!-- Buttons for Error State -->
      <div
        class="flex flex-col gap-3 mt-6"
        v-else-if="
          $stores.bookmaker?.state?.bookmakerData?.[
            `${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmaker}_${$stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmakerId}`
          ]?.error
        "
      >
        <button
          class="w-full py-4 px-6 text-white font-bold text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden group bg-secondary"
          @click="
            $bus.emit($constants.EVENTS.BOOKMAKER.REQUEST_BOOKMAKER_ID_CHANGE, {
              bookmakerId:
                $stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmakerId,
              bookmaker:
                $stores.bookmaker?.state?.bookmakerIdConfirmation?.bookmaker
            })
          "
        >
          <span class="relative z-10">Changer d'ID</span>
        </button>
        <button
          class="w-full py-4 px-6 font-semibold rounded-2xl border-2 transition-all duration-300 flex items-center justify-center gap-3 hover:bg-gray-50 active:scale-[0.98] border-gray-300 text-gray-600"
          @click="() => $refs.modal.hide()"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Retour
        </button>
      </div>
    </div>
  </DialogComponent>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'BookmakerAccountConfirm'
});
</script>
